import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
    SupportAgent, 
    Chat, 
    Email, 
    Phone, 
    Schedule, 
    Troubleshoot,
    BugReport,
    Article
} from '@mui/icons-material';
import SupportTicketForm from '../components/support/SupportTicketForm';

const TechnicalSupport = () => {
    return (
        <>
            <Helmet>
                <title>Support Technique - Netikash Developer Portal</title>
                <meta name="description" content="Support technique Netikash : assistance pour les développeurs, tickets de support, dépannage et ressources de résolution de problèmes." />
                <meta property="og:title" content="Support Technique - Netikash" />
                <link rel="canonical" href="https://dev.netikash.com/technical-support" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                                <SupportAgent className="mr-2" />
                                <span className="font-semibold text-lg">SUPPORT TECHNIQUE</span>
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                Nous sommes là pour vous aider
                            </h1>
                            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                                Notre équipe d'experts techniques est disponible pour résoudre vos problèmes et répondre à vos questions
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Contact Methods */}
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Modes de Contact</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
                                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Email className="text-blue-600 text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
                                <p className="text-gray-600 mb-4 text-sm">
                                    Réponse sous 24-48h pour les questions générales
                                </p>
                                <a
                                    href="mailto:support@netikash.com"
                                    className="text-amber-600 hover:text-amber-700 font-medium"
                                >
                                    support@netikash.com
                                </a>
                            </div>

                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Phone className="text-green-600 text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Téléphone</h3>
                                <p className="text-gray-600 mb-4 text-sm">
                                    Disponible du lundi au vendredi, 9h-18h
                                </p>
                                <a
                                    href="tel:+243994896388"
                                    className="text-amber-600 hover:text-amber-700 font-medium"
                                >
                                    +243 994 896 388
                                </a>
                            </div>

                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
                                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Chat className="text-purple-600 text-3xl" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Chat en direct</h3>
                                <p className="text-gray-600 mb-4 text-sm">
                                    Disponible prochainement
                                </p>
                                <span className="text-gray-400 font-medium text-sm">
                                    Bientôt disponible
                                </span>
                            </div>
                        </div>
                    </section>

                    {/* Support Ticket Form */}
                    <section className="mb-12">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                            <div className="flex items-center space-x-4 mb-6">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                    <BugReport className="text-amber-600" />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-bold text-gray-900">Soumettre un ticket de support</h2>
                                    <p className="text-gray-600 mt-1">
                                        Créez un ticket pour signaler un problème ou poser une question technique
                                    </p>
                                </div>
                            </div>
                            <SupportTicketForm />
                        </div>
                    </section>

                    {/* Troubleshooting Resources */}
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Ressources de Dépannage</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <Link
                                to="/documentation?section=http-status-codes"
                                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
                            >
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                                        <Troubleshoot className="text-red-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Codes d'Erreur HTTP</h3>
                                </div>
                                <p className="text-gray-700">
                                    Comprendre les codes de statut HTTP et comment les résoudre
                                </p>
                            </Link>

                            <Link
                                to="/error-codes"
                                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
                            >
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                                        <Article className="text-orange-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Codes d'Erreur API</h3>
                                </div>
                                <p className="text-gray-700">
                                    Liste complète des codes d'erreur et leurs solutions
                                </p>
                            </Link>

                            <Link
                                to="/faq"
                                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
                            >
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                        <Article className="text-blue-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">FAQ</h3>
                                </div>
                                <p className="text-gray-700">
                                    Réponses aux questions fréquemment posées
                                </p>
                            </Link>

                            <Link
                                to="/documentation"
                                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
                            >
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                        <Article className="text-green-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">Documentation</h3>
                                </div>
                                <p className="text-gray-700">
                                    Documentation complète de l'API avec exemples
                                </p>
                            </Link>
                        </div>
                    </section>

                    {/* Support Hours */}
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                        <div className="flex items-start space-x-4">
                            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Schedule className="text-amber-600" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">Horaires de Support</h3>
                                <div className="space-y-3 text-gray-700">
                                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                                        <p className="font-semibold text-gray-900 mb-2">Support par Email</p>
                                        <p className="text-sm">Lundi - Vendredi : 9h00 - 18h00 (GMT+2)</p>
                                        <p className="text-sm">Temps de réponse moyen : 24-48h</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                                        <p className="font-semibold text-gray-900 mb-2">Support par Téléphone</p>
                                        <p className="text-sm">Lundi - Vendredi : 9h00 - 18h00 (GMT+2)</p>
                                        <p className="text-sm">Réponse immédiate</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                                        <p className="font-semibold text-gray-900 mb-2">Support Urgent (Production)</p>
                                        <p className="text-sm">24/7 pour les clients Enterprise</p>
                                        <p className="text-sm">Contactez votre responsable de compte</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Escalation */}
                    <section className="mt-12 bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Escalade vers Support Commercial</h3>
                        <p className="text-gray-700 mb-6">
                            Pour les questions commerciales, tarification, ou partenariats, contactez notre équipe commerciale.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                        >
                            Contact Commercial
                        </Link>
                    </section>
                </div>
            </div>
        </>
    );
};

export default TechnicalSupport;